let csrfToken = ''
let telegramInitData = ''

interface ErrorResponse { error?: string }

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

export function setCsrfToken(value: string): void { csrfToken = value }
export function getCsrfToken(): string { return csrfToken }
export function setTelegramInitData(value: string): void { telegramInitData = value }

function requestHeaders(url: string, options: RequestInit): Headers {
  const headers = new Headers(options.headers)
  if (options.body !== undefined && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  if (options.method && options.method !== 'GET' && options.method !== 'HEAD') headers.set('X-CSRF-Token', csrfToken)
  if (telegramInitData
    && options.method === 'POST'
    && /^\/api\/public\/speakers\/[^/]+\/(?:voice-)?questions(?:\/stream)?$/u.test(url)) {
    headers.set('X-Telegram-Init-Data', telegramInitData)
  }
  return headers
}

async function responseError(response: Response, url: string): Promise<ApiError> {
  const payload = await response.json().catch(() => ({})) as ErrorResponse
  if (response.status === 401 && url.startsWith('/api/admin/')) {
    window.dispatchEvent(new CustomEvent('spikerskie:unauthorized'))
  }
  return new ApiError(payload.error || `Ошибка сервера (${response.status})`, response.status)
}

export async function api<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = requestHeaders(url, options)
  const response = await fetch(url, { ...options, headers, credentials: 'same-origin' })
  if (!response.ok) throw await responseError(response, url)
  return response.json() as Promise<T>
}

export interface SseMessage {
  event: string
  data: string
}

function parseSseEvent(value: string): SseMessage | null {
  let event = 'message'
  const data: string[] = []
  for (const line of value.split(/\r?\n/u)) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /u, ''))
  }
  return data.length ? { event, data: data.join('\n') } : null
}

export async function apiSse(
  url: string,
  options: RequestInit,
  onMessage: (message: SseMessage) => void | Promise<void>,
): Promise<void> {
  const headers = requestHeaders(url, options)
  headers.set('Accept', 'text/event-stream')
  const response = await fetch(url, { ...options, headers, credentials: 'same-origin' })
  if (!response.ok) throw await responseError(response, url)
  if (!response.body) throw new ApiError('Сервер не открыл поток ответа.', response.status)

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const dispatch = async (event: string): Promise<void> => {
    const parsed = parseSseEvent(event)
    if (parsed) await onMessage(parsed)
  }
  try {
    for (;;) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      // Границу событий ищем и измеряем одним и тем же совпадением: перевод
      // строки бывает смешанным, и «на глаз» её длина считается неверно.
      let boundary = /\r?\n\r?\n/u.exec(buffer)
      while (boundary) {
        const event = buffer.slice(0, boundary.index)
        buffer = buffer.slice(boundary.index + boundary[0].length)
        await dispatch(event)
        boundary = /\r?\n\r?\n/u.exec(buffer)
      }
      if (done) break
    }
    if (buffer.trim()) await dispatch(buffer)
  } catch (error) {
    await reader.cancel().catch(() => undefined)
    throw error
  }
}

export function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : 'Не удалось выполнить действие.'
}
